import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  FileText,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Eye,
  Calendar,
  Users,
  Tag,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import proposalService, { Proposal } from '../services/proposalService';

const STATUS_LABELS: Record<string, string> = {
  draft: '작성중', writing: '작성중', reviewing: '검토중',
  submitted: '제출완료', selected: '선정', rejected: '탈락',
};

const STATUS_FILTER: Record<string, string[]> = {
  '진행중': ['draft', 'writing'],
  '검토중': ['reviewing'],
  '완료': ['submitted', 'selected', 'rejected'],
};

function toKoreanStatus(s: string): string { return STATUS_LABELS[s] || s; }

const Projects: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [projects, setProjects] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await proposalService.getProposals({ page_size: 50 });
      const items = Array.isArray(response) ? response : (response.items || []);
      setProjects(items);
    } catch (err: any) {
      console.error('Failed to fetch projects:', err);
      setError('프로젝트 목록을 불러오는데 실패했습니다.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const filteredProjects = projects.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || (p.title || '').toLowerCase().includes(q) ||
      ((p as any).grant_title || '').toLowerCase().includes(q);

    if (selectedFilter === 'all') return matchesSearch;
    const statuses = STATUS_FILTER[selectedFilter];
    return matchesSearch && statuses?.includes(p.status);
  });

  const getStatusColor = (status: string) => {
    const label = toKoreanStatus(status);
    switch (label) {
      case '선정': return 'bg-green-100 text-green-800';
      case '제출완료': return 'bg-blue-100 text-blue-800';
      case '검토중': return 'bg-yellow-100 text-yellow-800';
      case '작성중': return 'bg-gray-100 text-gray-800';
      case '탈락': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case '사업계획서': case '공모사업계획서': return '🏆';
      case '연구보고서': return '📊';
      case '정책보고서': return '📈';
      case '제안입찰제안서': return '⚡';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
        <span className="ml-3 text-gray-600">프로젝트 목록을 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">프로젝트</h1>
          <p className="text-gray-600 mt-2">모든 프로젝트를 관리하고 진행 상황을 확인하세요</p>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={fetchProjects} className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-1" />
            새로고침
          </button>
          <Link to="/proposal-generator" className="btn-primary">
            <Plus className="h-5 w-5 mr-2" />
            새 프로젝트
          </Link>
        </div>
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="프로젝트 검색..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">모든 상태</option>
            <option value="진행중">진행중</option>
            <option value="검토중">검토중</option>
            <option value="완료">완료</option>
          </select>
        </div>
      </div>

      {/* 프로젝트 그리드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="card hover:shadow-medium transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{getTypeIcon(project.document_type)}</span>
                <div>
                  <span className="text-xs text-gray-500">{project.document_type || '제안서'}</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(project.status)}`}>
                    {toKoreanStatus(project.status)}
                  </span>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.title}</h3>
            {(project as any).grant_title && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{(project as any).grant_title}</p>
            )}

            {/* 진행률 */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">진행률</span>
                <span className="font-medium">{project.progress || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${project.progress || 0}%` }}
                />
              </div>
            </div>

            {/* 메타 */}
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Calendar className="h-4 w-4 mr-1" />
              <span>{project.updated_at ? new Date(project.updated_at).toLocaleDateString('ko-KR') : (project as any).last_updated || ''}</span>
              {project.evaluation_score != null && (
                <span className="ml-3 text-emerald-600 font-medium">평가: {project.evaluation_score}점</span>
              )}
            </div>

            {/* 액션 */}
            <div className="flex items-center space-x-2">
              <Link
                to={`/projects?proposal=${project.id}`}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
              >
                <Eye className="h-4 w-4 mr-1" />
                보기
              </Link>
              <Link to={`/proposal-generator?edit=${project.id}`} className="p-2 text-gray-400 hover:text-gray-600">
                <Edit className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {!loading && filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {projects.length === 0 ? '아직 프로젝트가 없습니다' : '프로젝트를 찾을 수 없습니다'}
          </h3>
          <p className="text-gray-600 mb-4">
            {projects.length === 0 ? '새 제안서를 작성하여 시작하세요' : '검색어나 필터를 변경해보세요'}
          </p>
          <Link to="/proposal-generator" className="btn-primary inline-flex">
            <Plus className="h-5 w-5 mr-2" />
            첫 프로젝트 만들기
          </Link>
        </div>
      )}
    </div>
  );
};

export default Projects;
